import { dbGet, dbGetAllByIndex, dbPut, dbUpdate } from './db';
import { notifyReviewStore } from './events';
import { AssetKind, BlobKind, FileVariant, IAssetState, IBlobRecord, ILocalBatch, ILocalFile } from './types';

/**
 * Downloads a batch's server images into IndexedDB so the review never waits on the
 * network. Review images (draft, original, mockup) go first, file by file; the heavy
 * background-removed objects (only needed for editing) follow.
 */

const CONCURRENCY = 3;
/** A request that delivers no bytes for this long is hung: abort it instead of waiting forever. */
const STALL_MS = 30 * 1000;
/** createImageBitmap can hang on odd files — the size is only informational. */
const MEASURE_TIMEOUT_MS = 15 * 1000;
const REVIEW_KINDS: AssetKind[] = ['draft', 'draftUpscaled', 'original', 'mockup'];
const EDIT_KINDS: AssetKind[] = ['object', 'objectUpscaled'];

export const blobKey = (fileId: string, kind: BlobKind) => `${fileId}:${kind}`;

export const assetUrl = (file: ILocalFile, kind: AssetKind): string | null => {
    switch (kind) {
        case 'draft':
            return file.draftPrintReadyUrl;
        case 'object':
            return file.backgroundRemovedUrl;
        case 'original':
            return file.originalUrl;
        case 'mockup':
            return file.mockupUrl;
        case 'draftUpscaled':
            return file.draftPrintReadyFileUpscaledUrl ?? null;
        case 'objectUpscaled':
            return file.backgroundRemovedFileUpscaledUrl ?? null;
        default:
            return null;
    }
};

/** Download state of an asset; files stored before a kind existed simply have no entry for it. */
export const assetState = (file: ILocalFile, kind: AssetKind): IAssetState['state'] =>
    file.assets?.[kind]?.state ?? (assetUrl(file, kind) ? 'pending' : 'missing');

export const hasUpscaled = (file: ILocalFile) => !!file.draftPrintReadyFileUpscaledUrl;

/** What becomes the final print file for the given variant. */
export type FinalChoice = 'edit' | 'remoteEdit' | 'upscaled' | 'draft';

/**
 * - `edit`       — a manual edit made in this browser from that variant (uploaded on save);
 * - `remoteEdit` — a manual edit uploaded from another browser (already the server final);
 * - `upscaled`   — the AI-upscaled draft as is (saved as hasManualEdits + its URL);
 * - `draft`      — the regular draft (the server copies it).
 */
export const finalChoice = (file: ILocalFile, variant: FileVariant): FinalChoice => {
    if (file.edit && (file.edit.variant ?? 'regular') === variant) return 'edit';
    if (file.hasManualEdits && !file.edit && file.sync.finalPrintReadyUrl && (file.decidedVariant ?? 'regular') === variant) return 'remoteEdit';
    return variant === 'upscaled' && hasUpscaled(file) ? 'upscaled' : 'draft';
};

/** Variant to show first: the decided one, else the one that was edited, else regular. */
export const defaultVariant = (file: ILocalFile): FileVariant => {
    const variant = file.decision ? file.decidedVariant : file.edit?.variant;
    return variant === 'upscaled' && hasUpscaled(file) ? 'upscaled' : 'regular';
};

export const getStoredBlob = async (fileId: string, kind: BlobKind): Promise<Blob | null> => {
    const rec = await dbGet<IBlobRecord>('blobs', blobKey(fileId, kind));
    return rec?.blob ?? null;
};

export const putStoredBlob = (file: Pick<ILocalFile, 'id' | 'batchId'>, kind: BlobKind, blob: Blob) =>
    dbPut<IBlobRecord>('blobs', { key: blobKey(file.id, kind), fileId: file.id, batchId: file.batchId, kind, blob });

const setAssetState = async (fileId: string, kind: AssetKind, state: IAssetState) => {
    await dbUpdate<ILocalFile>('files', fileId, (f) => (f ? { ...f, assets: { ...f.assets, [kind]: state } } : undefined));
    notifyReviewStore();
};

const measure = async (blob: Blob): Promise<{ width?: number; height?: number }> => {
    try {
        const bmp = await Promise.race([
            createImageBitmap(blob),
            new Promise<null>((resolve) => setTimeout(() => resolve(null), MEASURE_TIMEOUT_MS)),
        ]);
        if (!bmp) return {};
        const size = { width: bmp.width, height: bmp.height };
        bmp.close();
        return size;
    } catch (e) {
        return {};
    }
};

/** Bytes received by the downloads in flight — the "still moving" signal for the UI. */
const liveDownloads = new Map<string, { batchId: string; loaded: number }>();

export const batchDownloadActivity = (batchId: string) => {
    let loaded = 0;
    let active = 0;
    liveDownloads.forEach((d) => {
        if (d.batchId !== batchId) return;
        loaded += d.loaded;
        active += 1;
    });
    return { loaded, active };
};

/**
 * `cache: 'reload'` skips the HTTP cache: an earlier plain <img> load of the same URL is
 * cached without CORS headers and would make this CORS request fail.
 */
const fetchAsset = async (url: string, onProgress?: (loaded: number) => void): Promise<Blob> => {
    /** One request, read as a stream so a connection that stops delivering bytes is aborted. */
    const attempt = async (target: string): Promise<Blob> => {
        const controller = new AbortController();
        let stalled = false;
        let timer: ReturnType<typeof setTimeout> | undefined;
        const arm = () => {
            if (timer) clearTimeout(timer);
            timer = setTimeout(() => {
                stalled = true;
                controller.abort();
            }, STALL_MS);
        };
        try {
            arm();
            const res = await fetch(target, { mode: 'cors', cache: 'reload', signal: controller.signal });
            if (!res.ok) throw Object.assign(new Error(`HTTP ${res.status}`), { http: true });
            if (!res.body) return await res.blob();
            const reader = res.body.getReader();
            const chunks: Uint8Array[] = [];
            let loaded = 0;
            for (;;) {
                arm();
                const { done, value } = await reader.read();
                if (done) break;
                if (value) {
                    chunks.push(value);
                    loaded += value.byteLength;
                    onProgress?.(loaded);
                }
            }
            return new Blob(chunks, { type: res.headers.get('Content-Type') || '' });
        } catch (e: any) {
            if (stalled) throw new Error(`No data for ${STALL_MS / 1000}s — download stalled`);
            throw e;
        } finally {
            if (timer) clearTimeout(timer);
        }
    };
    try {
        return await attempt(url);
    } catch (e: any) {
        // A server answer (403/404…) won't change; a network / CORS / stalled failure gets one more try.
        if (e?.http) throw e;
        onProgress?.(0);
        return await attempt(`${url}${url.includes('?') ? '&' : '?'}cors=${Date.now()}`);
    }
};

const inFlight = new Map<string, Promise<Blob | null>>();

type PackKind = Pick<ILocalBatch, 'serverStatus' | 'viewOnly'> | null | undefined;

/** Done and view-only packs are only looked at: they show the server finals and never need the edit sources. */
export const isViewPack = (batch: PackKind) => batch?.serverStatus === 'done' || !!batch?.viewOnly;

export const packAssetKinds = (batch: PackKind): AssetKind[] =>
    isViewPack(batch) ? ['draft', 'original', 'mockup'] : [...REVIEW_KINDS, ...EDIT_KINDS];

/** The server final is shown for view packs, and for files whose manual edit was uploaded from another browser. */
export const needsServerFinal = (batch: PackKind, f: ILocalFile) =>
    !!f.sync.finalPrintReadyUrl && (isViewPack(batch) || (f.hasManualEdits && !f.edit));

/** State of the stored server final; a different finalPrintReadyUrl means it has to be fetched again. */
export const serverFinalState = (f: ILocalFile): IAssetState['state'] =>
    f.finalAsset && f.finalAsset.url === f.sync.finalPrintReadyUrl ? f.finalAsset.state : 'pending';

/**
 * Download progress of a pack. `complete` — nothing is left to fetch (failed images
 * included), which is when the pack may be opened.
 */
export const packDownloadProgress = (batch: PackKind, files: ILocalFile[]) => {
    const kinds = packAssetKinds(batch);
    let total = 0;
    let done = 0;
    let failed = 0;
    let pending = 0;
    let readyFiles = 0;
    let reviewPending = 0;
    let bytes = 0;
    files.forEach((f) => {
        const states = kinds.map((k) => assetState(f, k));
        if (needsServerFinal(batch, f)) states.push(serverFinalState(f));
        if (states.every((st) => st === 'ok' || st === 'missing')) readyFiles += 1;
        // Everything except the edit sources is what the review itself shows.
        kinds.forEach((k, i) => {
            if (!EDIT_KINDS.includes(k) && states[i] === 'pending') reviewPending += 1;
            if (states[i] === 'ok') bytes += f.assets?.[k]?.bytes ?? 0;
        });
        if (needsServerFinal(batch, f)) {
            if (serverFinalState(f) === 'pending') reviewPending += 1;
            else if (serverFinalState(f) === 'ok') bytes += f.finalAsset?.bytes ?? 0;
        }
        states.forEach((st) => {
            if (st === 'missing') return;
            total += 1;
            if (st === 'ok') done += 1;
            else if (st === 'error') failed += 1;
            else pending += 1;
        });
    });
    return {
        total,
        done,
        failed,
        pending,
        readyFiles,
        files: files.length,
        /** Bytes of the images already stored. */
        bytes,
        complete: files.length > 0 && pending === 0,
        /** The review images are all here; only edit sources may still be downloading. */
        reviewComplete: files.length > 0 && reviewPending === 0,
    };
};

const setFinalAssetState = async (fileId: string, state: IAssetState) => {
    await dbUpdate<ILocalFile>('files', fileId, (f) => (f ? { ...f, finalAsset: state } : undefined));
    notifyReviewStore();
};

/** Download the reviewed file as the server keeps it (finalPrintReadyUrl). */
export const ensureServerFinal = (file: ILocalFile): Promise<Blob | null> => {
    const key = blobKey(file.id, 'serverFinal');
    const running = inFlight.get(key);
    if (running) return running;
    const task = (async () => {
        const fresh = (await dbGet<ILocalFile>('files', file.id)) ?? file;
        const url = fresh.sync.finalPrintReadyUrl;
        if (!url) return null;
        if (serverFinalState(fresh) === 'ok') return getStoredBlob(file.id, 'serverFinal');
        try {
            liveDownloads.set(key, { batchId: fresh.batchId, loaded: 0 });
            const blob = await fetchAsset(url, (loaded) => liveDownloads.set(key, { batchId: fresh.batchId, loaded }));
            const size = await measure(blob);
            await putStoredBlob(fresh, 'serverFinal', blob);
            await setFinalAssetState(file.id, { state: 'ok', url, bytes: blob.size, ...size });
            return blob;
        } catch (e: any) {
            await setFinalAssetState(file.id, { state: 'error', url, error: e?.message || 'Download failed' });
            return null;
        } finally {
            liveDownloads.delete(key);
        }
    })().finally(() => inFlight.delete(key));
    inFlight.set(key, task);
    return task;
};

/** The stored asset, downloading it right now when it isn't on this device yet. */
export const ensureAsset = (file: ILocalFile, kind: AssetKind): Promise<Blob | null> => {
    const key = blobKey(file.id, kind);
    const running = inFlight.get(key);
    if (running) return running;
    const task = (async () => {
        const measured = kind === 'draft' || kind === 'draftUpscaled' || kind === 'original';
        const existing = await getStoredBlob(file.id, kind);
        if (existing) {
            // The record can lag behind the stored image (it was rewritten while a download ran):
            // without this the asset would stay "pending" forever and block the set.
            const fresh = await dbGet<ILocalFile>('files', file.id);
            if (fresh && fresh.assets?.[kind]?.state !== 'ok') {
                await setAssetState(file.id, kind, { state: 'ok', bytes: existing.size, ...(measured ? await measure(existing) : {}) });
            }
            return existing;
        }
        const url = assetUrl(file, kind);
        if (!url) {
            await setAssetState(file.id, kind, { state: 'missing' });
            return null;
        }
        try {
            liveDownloads.set(key, { batchId: file.batchId, loaded: 0 });
            const blob = await fetchAsset(url, (loaded) => liveDownloads.set(key, { batchId: file.batchId, loaded }));
            const size = measured ? await measure(blob) : {};
            await putStoredBlob(file, kind, blob);
            await setAssetState(file.id, kind, { state: 'ok', bytes: blob.size, ...size });
            return blob;
        } catch (e: any) {
            await setAssetState(file.id, kind, { state: 'error', error: e?.message || 'Download failed' });
            return null;
        } finally {
            liveDownloads.delete(key);
        }
    })().finally(() => inFlight.delete(key));
    inFlight.set(key, task);
    return task;
};

const runPool = async <T,>(items: T[], limit: number, worker: (item: T) => Promise<unknown>) => {
    let next = 0;
    const lanes = Array.from({ length: Math.min(limit, items.length) }, async () => {
        while (next < items.length) {
            const item = items[next];
            next += 1;
            await worker(item);
        }
    });
    await Promise.all(lanes);
};

const batchDownloads = new Map<string, Promise<void>>();
/** Sets whose records changed while their download was running — they get another pass. */
const rerunRequested = new Set<string>();

export const isBatchDownloading = (batchId: string) => batchDownloads.has(batchId);

/** Download every missing asset of a batch (idempotent; failed assets are retried). */
export const downloadBatchAssets = (batchId: string): Promise<void> => {
    const running = batchDownloads.get(batchId);
    if (running) {
        // The running pass picked its jobs from older records (e.g. before "Load to continue").
        rerunRequested.add(batchId);
        return running;
    }
    const pass = async () => {
        const batch = await dbGet<ILocalBatch>('batches', batchId);
        const files = (await dbGetAllByIndex<ILocalFile>('files', 'batchId', batchId)).sort((a, b) => a.index - b.index);
        const kinds = packAssetKinds(batch);
        const todo = (f: ILocalFile, kind: AssetKind) => kinds.includes(kind) && assetState(f, kind) !== 'ok' && assetState(f, kind) !== 'missing';
        const jobs: [ILocalFile, AssetKind | 'serverFinal'][] = [];
        files.forEach((f) => {
            if (needsServerFinal(batch, f) && serverFinalState(f) !== 'ok') jobs.push([f, 'serverFinal']);
            REVIEW_KINDS.forEach((k) => todo(f, k) && jobs.push([f, k]));
        });
        // Edit sources are heavy and only needed while the pack can still be edited.
        files.forEach((f) => EDIT_KINDS.forEach((k) => todo(f, k) && jobs.push([f, k])));
        notifyReviewStore();
        await runPool(jobs, CONCURRENCY, ([f, k]) => (k === 'serverFinal' ? ensureServerFinal(f) : ensureAsset(f, k)));
    };
    const task = (async () => {
        do {
            rerunRequested.delete(batchId);
            await pass();
        } while (rerunRequested.has(batchId));
    })()
        .catch((e) => console.warn(`Print batch ${batchId} download failed`, e))
        .finally(() => {
            batchDownloads.delete(batchId);
            rerunRequested.delete(batchId);
            notifyReviewStore();
        });
    batchDownloads.set(batchId, task);
    return task;
};

export const initialAssets = (f: {
    draftPrintReadyUrl: string | null;
    backgroundRemovedUrl: string | null;
    originalUrl: string | null;
    mockupUrl: string | null;
    draftPrintReadyFileUpscaledUrl?: string | null;
    backgroundRemovedFileUpscaledUrl?: string | null;
}): Record<AssetKind, IAssetState> => ({
    draft: { state: f.draftPrintReadyUrl ? 'pending' : 'missing' },
    object: { state: f.backgroundRemovedUrl ? 'pending' : 'missing' },
    original: { state: f.originalUrl ? 'pending' : 'missing' },
    mockup: { state: f.mockupUrl ? 'pending' : 'missing' },
    draftUpscaled: { state: f.draftPrintReadyFileUpscaledUrl ? 'pending' : 'missing' },
    objectUpscaled: { state: f.backgroundRemovedFileUpscaledUrl ? 'pending' : 'missing' },
});
